<template>
  <div v-if="showDialog" class="projects-module">
    <div class="projects-container">
      <div class="projects-content" @wheel.stop>
        <div v-if="projects.length === 0" class="empty-state">
          <EmptyState
            title="Sin proyectos"
            description="No hay proyectos asignados"
          />
        </div>
        <div v-else class="projects-list">
          <Card
            v-for="project in projects"
            :key="project.id"
            class="project-card"
          >
            <template #header>
              <div class="project-header">
                <h3 class="project-name">{{ project.shortName }}</h3>
                <Chip
                  v-if="project.internalCode"
                  :label="project.internalCode"
                  class="project-code"
                />
              </div>
            </template>
            <template #content>
              <div class="project-details">
                <p v-if="project.description" class="project-description">
                  {{ project.description }}
                </p>
                <div class="project-info">
                  <div v-if="project.fromDate" class="info-row">
                    <span class="info-label">{{ t('wkt.client.projects.from_date') }}:</span>
                    <span class="info-value">{{ formatProjectDate(project.fromDate) }}</span>
                  </div>
                  <div v-if="project.toDate" class="info-row">
                    <span class="info-label">{{ t('wkt.client.projects.to_date') }}:</span>
                    <span class="info-value">{{ formatProjectDate(project.toDate) }}</span>
                  </div>
                  <div v-if="project.client" class="info-row">
                    <span class="info-label">{{ t('wkt.client.projects.client') }}:</span>
                    <span class="info-value">{{ project.client }}</span>
                  </div>
                  <div v-if="project.address" class="info-row">
                    <span class="info-label">{{ t('wkt.client.projects.address') }}:</span>
                    <span class="info-value">{{ project.address }}</span>
                  </div>
                  <div v-if="project.contactPerson" class="info-row">
                    <span class="info-label">{{ t('wkt.client.projects.contact_person') }}:</span>
                    <span class="info-value">{{ project.contactPerson }}</span>
                  </div>
                  <div v-if="project.contactEmail" class="info-row">
                    <span class="info-label">{{ t('wkt.client.projects.contact_email') }}:</span>
                    <span class="info-value">{{ project.contactEmail }}</span>
                  </div>
                  <div v-if="project.contactPhone" class="info-row">
                    <span class="info-label">{{ t('wkt.client.projects.contact_phone') }}:</span>
                    <span class="info-value">{{ project.contactPhone }}</span>
                  </div>
                  <div v-if="project.observations" class="info-row">
                    <span class="info-label">{{ t('wkt.client.projects.observations') }}:</span>
                    <span class="info-value">{{ project.observations }}</span>
                  </div>
                  <div v-if="project.specialRequirements" class="info-row">
                    <span class="info-label">{{ t('wkt.client.projects.special_requirements') }}:</span>
                    <span class="info-value">{{ project.specialRequirements }}</span>
                  </div>
                </div>
              </div>
            </template>
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">

import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { gwtApi } from '@wkt/api/gwt/gwtAPI'
import { useDateFormatter } from '@wkt/composables/useDateFormatter'
import { useDateConverter } from '@wkt/composables/useDateConverter'
import EmptyState from '@wkt/components/common/EmptyState.vue'
import type { Project } from '@wkt/api/gwt/gwtDTO'

// Composables
const { t } = useI18n()
const { formatOnlyDate } = useDateFormatter()
const { convertSimpleDateToDate } = useDateConverter()
// State
const showDialog = ref(false)
const projects = ref<Project[]>([])
// Methods
function formatProjectDate(date: any): string {
  if (!date) return ''
  const converted = convertSimpleDateToDate(date)
  return converted ? formatOnlyDate(converted) : ''
}

function onReveal() {
  open()
}

function open() {
  showDialog.value = true
  
  try {
    const sessionInfo = gwtApi.getSessionInformation()
    
    if (sessionInfo && sessionInfo.employeeProjects) {
      projects.value = sessionInfo.employeeProjects.filter(p => p.enabled !== false)
    } else {
      projects.value = []
    }
  } catch (error) {
    console.error('Error al cargar proyectos:', error)
    projects.value = []
  }
}

function close() {
  showDialog.value = false
}

// Exponer métodos para uso externo
defineExpose({
  open,
  close,
  onReveal
})
</script>

<style scoped>
.projects-module {
  position: absolute;
  top: 48px;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: calc(100vh - 48px);
  background: var(--white);
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 100;
  padding-top: 10px;
  padding-bottom: 20px;
  -webkit-overflow-scrolling: touch;
}
.projects-container {
    padding: 0.5rem;
}
.projects-content {
  max-width: 1200px;
  margin: 0 auto;
}
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
}
.projects-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.project-card {
  background: #FFFFFF;
  border: 1px solid var(--border-color);
  box-shadow: none;
}
.project-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
}
.project-name {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--black);
}
.project-code {
  background-color: var(--primary-color);
  color: var(--white);
  padding: 5px 15px;
}
/*.project-details {
  padding: 1rem;
}*/
.project-description {
  margin: 0 0 1rem 0;
  color: var(--grey-text);
  font-size: 1.1rem;
}
.project-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.info-row {
  display: flex;
  gap: 0.5rem;
  padding: 0.25rem 0;
}
.info-label {
  font-weight: 600;
  color: #495057;
  /*min-width: 150px;*/
}
.info-value {
  color: var(--grey-text);
  flex: 1;
  font-size: 1rem;
}

/* Ocultar botones de navegación de pestañas */
.p-tablist-next-button.p-tablist-nav-button,
:deep(.p-tablist-next-button.p-tablist-nav-button),
:deep(.p-tablist-prev-button.p-tablist-nav-button) {
  display: none !important;
}

/* Responsive */
@media (max-width: 768px) {
  .projects-module {
    top: 60px;
    height: calc(100vh - 60px);
  }
  /*.project-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }*/
  .info-row {
    /*flex-direction: column;*/
    gap: 0.5rem;
  }
  .info-label {
    min-width: auto;
  }
}


/* Ocultar en impresión */
@media print {
  .projects-module {
    display: none !important;
  }
}
</style>